import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs, orderBy, limit, addDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { useAuth } from '../contexts/AuthContext';
import { createAdminNotificationMeta } from '../../utils/adminNotificationMeta';
import { FiAward, FiClock, FiBarChart2, FiStar } from 'react-icons/fi';
import { MathJax, MathJaxContext } from 'better-react-mathjax';
import { formatCustomTagsToLaTeX } from '../common/QuestionFormatter';

// MathJax configuration (can reuse the same one)
const mathJaxConfig = {
  loader: { load: ["[tex]/html"] },
  tex: {
    packages: { "[+]": ["html"] },
    inlineMath: [["$", "$"], ["\\(", "\\)"]], // $...$ and \(...\)
    displayMath: [["$$", "$$"], ["\\[", "\\]"]], // $$...$$ and \[...\]
    processEscapes: true,
    macros: { // Add support for \ce{} macro
      '\\ce': '{\\text{#1}}'
    }
  },
  options: { // Optional: Configure MathJax options if needed
    enableMenu: false, // Disable right-click MathJax menu
  },
};

function QuizResults() {
  const { quizId } = useParams();
  const [searchParams] = useSearchParams();
  const participantId = searchParams.get('participant');
  const score = searchParams.get('score');
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quiz, setQuiz] = useState(null);
  const [participant, setParticipant] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [leaderboardLoading, setLeaderboardLoading] = useState(true);
  const [leaderboardError, setLeaderboardError] = useState(null);
  const [showFlagModal, setShowFlagModal] = useState(false);
  const [currentFlaggedQuestion, setCurrentFlaggedQuestion] = useState(null);
  const [flaggedQuestions, setFlaggedQuestions] = useState(new Set());
  const [flagReason, setFlagReason] = useState('');

  const flagReasons = [
    'Incorrect answer',
    'Unclear question',
    'Grammatical error',
    'Technical issue',
    'Other'
  ];

  const handleFlagQuestion = (questionId) => {
    setCurrentFlaggedQuestion(questionId);
    setShowFlagModal(true);
  };

  const submitFlag = async () => {
    if (!flagReason) return;

    try {
      await addDoc(collection(db, 'flagged_questions'), {
        questionId: currentFlaggedQuestion,
        userId: auth.currentUser.uid,
        reason: flagReason,
        timestamp: serverTimestamp(),
        status: 'pending',
        quizId: quizId,
        questionNumber: quiz.questions.findIndex(q => q.id === currentFlaggedQuestion) + 1,
        userAnswer: participant.answers[quiz.questions.findIndex(q => q.id === currentFlaggedQuestion)],
        correctAnswer: quiz.questions.find(q => q.id === currentFlaggedQuestion).correctOption,
        ...createAdminNotificationMeta({
          type: 'flagged_question',
          summary: `Flagged quiz question ${currentFlaggedQuestion} by ${auth.currentUser.displayName || auth.currentUser.email || 'a user'}`,
        }),
      });

      setFlaggedQuestions(prev => new Set([...prev, currentFlaggedQuestion]));
      setShowFlagModal(false);
      setFlagReason('');
      setCurrentFlaggedQuestion(null);
    } catch (error) {
      console.error('Error submitting flag:', error);
    }
  };

  useEffect(() => {
    let isMounted = true;

    const fetchResults = async () => {
      try {
        // Fetch quiz data
        const quizRef = doc(db, 'quizzes', quizId);
        const quizDoc = await getDoc(quizRef);

        if (!quizDoc.exists()) {
          throw new Error('Quiz not found');
        }

        // Fetch participant data
        const participantRef = doc(db, 'public-quiz-participants', participantId);
        const participantDoc = await getDoc(participantRef);

        if (!participantDoc.exists()) {
          throw new Error('Results not found');
        }

        const participantData = participantDoc.data();

        if (isMounted) {
          setQuiz(quizDoc.data());
          setParticipant(participantData);
          setLoading(false);
        }

        // Fetch leaderboard data
        try {
          const participantsRef = collection(db, 'public-quiz-participants');
          const leaderboardQuery = query(
            participantsRef,
            where('quizId', '==', quizId),
            where('completed', '==', true),
            orderBy('score', 'desc'),
            limit(50)
          );

          const leaderboardDocs = await getDocs(leaderboardQuery);
          
          // Process leaderboard to get unique users with best scores
          const uniqueUserScores = new Map();
          leaderboardDocs.forEach(doc => {
            const data = doc.data();
            const existingEntry = uniqueUserScores.get(data.userId);
            
            if (!existingEntry || 
                (data.score > existingEntry.score || 
                 (data.score === existingEntry.score && 
                  data.completedAt.toDate() - data.startedAt.toDate() < 
                  existingEntry.completedAt.toDate() - existingEntry.startedAt.toDate()))) {
              uniqueUserScores.set(data.userId, {
                ...data,
                timeTaken: (data.completedAt.toDate() - data.startedAt.toDate()) / 1000,
                id: doc.id
              });
            }
          });

          // Convert to array, sort, and take top 5
          const topScores = Array.from(uniqueUserScores.values())
            .sort((a, b) => {
              if (b.score !== a.score) return b.score - a.score;
              return a.timeTaken - b.timeTaken;
            })
            .slice(0, 5);

          if (isMounted) {
            setLeaderboard(topScores);
          }
        } catch (leaderboardError) {
          console.error('Error fetching leaderboard:', leaderboardError);
          if (isMounted) {
            setLeaderboardError(leaderboardError.message);
          }
        } finally {
          if (isMounted) {
            setLeaderboardLoading(false);
          }
        }

      } catch (error) {
        console.error('Error fetching results:', error);
        if (isMounted) {
          setError(error.message);
          setLoading(false);
        }
      }
    };

    fetchResults();

    return () => {
      isMounted = false;
    };
  }, [quizId, participantId]);

  const getScoreColor = (score) => {
    if (score >= 75) return 'text-green-500';
    if (score >= 50) return 'text-yellow-500';
    return 'text-red-500';
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}m ${remainingSeconds}s`;
  };

  const calculateTimeTaken = (startTime, endTime) => {
    if (!startTime || !endTime) return 0;
    const start = startTime.toDate();
    const end = endTime.toDate();
    return Math.round((end - start) / 1000); // Returns time in seconds
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[rgb(130,88,18)]"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4">
        <div className="text-red-500 mb-4">{error}</div>
        <button
          onClick={() => navigate('/dashboard')}
          className="px-4 py-2 bg-[rgb(130,88,18)] text-white rounded hover:bg-[rgb(110,68,0)]"
        >
          Return to Dashboard
        </button>
      </div>
    );
  }

  return (
    <MathJaxContext>
      <div className="max-w-4xl mx-auto p-4">
        <div className="bg-white rounded-lg shadow-md p-6">
          {/* Personal Score Section */}
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold mb-2">Quiz Results</h1>
            <div className={`text-4xl font-bold ${getScoreColor(score)}`}>
              {score}%
            </div>
            <div className="text-gray-600 mt-2">
              {score >= 75 ? 'Excellent work!' : 
               score >= 50 ? 'Good effort!' : 
               'Keep practicing!'}
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <div className="bg-gray-50 p-4 rounded-lg text-center">
              <FiAward className="w-6 h-6 mx-auto mb-2 text-[rgb(130,88,18)]" />
              <div className="text-sm text-gray-600">Your Score</div>
              <div className="font-bold">{score}%</div>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg text-center">
              <FiBarChart2 className="w-6 h-6 mx-auto mb-2 text-[rgb(130,88,18)]" />
              <div className="text-sm text-gray-600">Questions</div>
              <div className="font-bold">{quiz?.questions?.length || 0}</div>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg text-center">
              <FiClock className="w-6 h-6 mx-auto mb-2 text-[rgb(130,88,18)]" />
              <div className="text-sm text-gray-600">Time Taken</div>
              <div className="font-bold">
                {formatTime(calculateTimeTaken(participant?.startedAt, participant?.completedAt))}
              </div>
            </div>
          </div>

          {/* Missed/Correct Questions Section */}
          {quiz?.questions && participant?.answers && (
            <div className="mb-8">
              <h2 className="text-xl font-bold mb-4 text-center">Your Answers</h2>
              <ul className="space-y-6">
                {quiz.questions.map((q, idx) => {
                  const userAnswerIdx = participant.answers[idx];
                  const isCorrect = userAnswerIdx === q.correctOption;
                  return (
                    <li key={idx} className={`p-4 rounded-lg shadow border ${isCorrect ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}> 
                      <div className="flex justify-between items-start mb-2">
                        <div className="font-semibold">Q{idx + 1}: <MathJax inline={true}>{formatCustomTagsToLaTeX(q.question)}</MathJax></div>
                        <button
                          onClick={() => handleFlagQuestion(q.id)}
                          className={`p-2 rounded-full ${
                            flaggedQuestions.has(q.id)
                              ? 'text-red-500'
                              : 'text-gray-400 hover:text-red-500'
                          }`}
                          title="Flag Question"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9" />
                          </svg>
                        </button>
                      </div>
                      <div className="ml-4">
                        <span className="font-medium">Your Answer:</span> {typeof userAnswerIdx === 'number' ? <MathJax inline={true}>{formatCustomTagsToLaTeX(q.options[userAnswerIdx])}</MathJax> : <span className="italic text-gray-400">No answer</span>}
                        <span className={`ml-4 px-2 py-1 rounded text-xs ${isCorrect ? 'bg-green-200 text-green-800' : 'bg-red-200 text-red-800'}`}>{isCorrect ? 'Correct' : 'Missed'}</span>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {/* Flag Question Modal */}
          {showFlagModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
              <div className="bg-white rounded-lg p-6 max-w-md w-full">
                <h3 className="text-xl font-semibold mb-4">Flag Question</h3>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reason for Flagging
                  </label>
                  <select
                    value={flagReason}
                    onChange={(e) => setFlagReason(e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-iof focus:ring-iof"
                  >
                    <option value="">Select a reason</option>
                    {flagReasons.map((reason) => (
                      <option key={reason} value={reason}>
                        {reason}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex justify-end space-x-4">
                  <button
                    onClick={() => {
                      setShowFlagModal(false);
                      setFlagReason('');
                      setCurrentFlaggedQuestion(null);
                    }}
                    className="px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={submitFlag}
                    disabled={!flagReason}
                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
                  >
                    Submit Flag
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Leaderboard Section */}
          <div className="mb-8">
            <h2 className="text-xl font-bold mb-4 text-center">Leaderboard</h2>
            {leaderboardLoading ? (
                <div className="flex justify-center items-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[rgb(130,88,18)]"></div></div>
            ) : leaderboardError ? (
                <p className="text-red-500 text-center">{leaderboardError}</p>
            ) : leaderboard.length === 0 ? (
                 <p className="text-gray-500 text-center">No participants on the leaderboard yet.</p>
            ) : (
                <div className="overflow-x-auto">
                    <table className="min-w-full bg-white rounded-lg shadow overflow-hidden">
                        <thead className="bg-gray-200">
                            <tr>
                                <th className="py-2 px-4 text-left text-sm font-semibold text-gray-600">Rank</th>
                                <th className="py-2 px-4 text-left text-sm font-semibold text-gray-600">User</th>
                                <th className="py-2 px-4 text-left text-sm font-semibold text-gray-600">Score (%)</th>
                                <th className="py-2 px-4 text-left text-sm font-semibold text-gray-600">Time</th>
                            </tr>
                        </thead>
                        <tbody>
                            {leaderboard.map((entry, index) => (
                                <tr key={entry.id} className={`${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'} ${entry.userId === currentUser?.uid ? 'font-bold bg-yellow-100' : ''}`}>
                                    <td className="py-2 px-4 text-sm text-gray-700">{index + 1}</td>
                                    <td className="py-2 px-4 text-sm text-gray-700">{entry.userName || '****'}</td>
                                    <td className="py-2 px-4 text-sm text-gray-700">{entry.score}</td>
                                    <td className="py-2 px-4 text-sm text-gray-700">{formatTime(entry.timeTaken)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="mt-8 flex justify-center space-x-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="px-6 py-2 bg-gray-200 rounded hover:bg-gray-300"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    </MathJaxContext>
  );
}

export default QuizResults; 